import React, { useEffect, useRef, useState } from 'react';
import {
  HISTORICAL_TEST_WINDOW_END_SECONDS,
  HISTORICAL_TEST_WINDOW_START_SECONDS,
  HVAC_OPERATION_START_SECONDS,
  HistoricalService,
  PlaybackStatus,
} from '../services/HistoricalService';

const SPEEDS = [1, 2, 5, 10, 30];
const MAX_VISUAL_PLAYBACK_SPEED = 30;
const TRACKING_SAFE_SPEED = 10;
const TESTBED_DATE = '20180601';
const TESTBED_START_SECONDS = HVAC_OPERATION_START_SECONDS;
const TESTBED_SPEED = 10;
const EP_STEP_SECONDS = 20 * 60;
const TESTBED_EP_STEPS = Math.floor((HISTORICAL_TEST_WINDOW_END_SECONDS - TESTBED_START_SECONDS) / EP_STEP_SECONDS);
const TRACKING_LOCKSTEP_PAUSE_AHEAD_SEC = EP_STEP_SECONDS * 2;
const TRACKING_LOCKSTEP_TIMEOUT_SEC = 45;

function secToHMS(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return [h, m, s].map(value => String(value).padStart(2, '0')).join(':');
}

function fmtDate(date: string) {
  return date.length === 8
    ? `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
    : date;
}

function trackingMessageKo(message?: string) {
  if (!message) return '';
  if (message.includes('waiting for required measured inputs')) {
    return '필수 실측 입력을 기다리는 중입니다.';
  }
  if (message.includes('Tracking Twin started')) {
    return 'Tracking이 시작되었습니다.';
  }
  if (message.includes('already running')) {
    return 'Tracking이 이미 실행 중입니다.';
  }
  return message;
}

function historicalEpoch(date: string, seconds: number) {
  const normalized = date.replaceAll('-', '');
  return new Date(
    Number(normalized.slice(0, 4)),
    Number(normalized.slice(4, 6)) - 1,
    Number(normalized.slice(6, 8)),
    0,
    0,
    seconds,
  ).getTime() / 1000;
}

function historicalFrameFromEpoch(epoch: number) {
  const dt = new Date(epoch * 1000);
  const date = [
    dt.getFullYear(),
    String(dt.getMonth() + 1).padStart(2, '0'),
    String(dt.getDate()).padStart(2, '0'),
  ].join('');
  const seconds = (dt.getHours() * 3600) + (dt.getMinutes() * 60) + dt.getSeconds();
  return { date, seconds };
}

interface HistoricalPlayerProps {
  activeTab?: string;
}

interface TrackingSyncState {
  active: boolean;
  label: string;
  running: boolean;
  outputStale: boolean;
  engineCompletedNeedsRestart: boolean;
  bufferReady: boolean;
  windowCount: number;
  targetWindowSec: number;
  effectiveWindowSec: number;
  latestOutputEpoch: number;
  callbackCount: number;
  outputCount: number;
  phase: string;
}

export const HistoricalPlayer: React.FC<HistoricalPlayerProps> = ({ activeTab = 'overview' }) => {
  const [open, setOpen] = useState(false);
  const [dates, setDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<PlaybackStatus>(HistoricalService.getStatus());
  const [selDate, setSelDate] = useState('');
  const [selSeconds, setSelSeconds] = useState(HISTORICAL_TEST_WINDOW_START_SECONDS);
  const [selSpeed, setSelSpeed] = useState(5);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [trackingSync, setTrackingSync] = useState<TrackingSyncState>({
    active: false,
    label: 'Tracking 대기',
    running: false,
    outputStale: false,
    engineCompletedNeedsRestart: false,
    bufferReady: false,
    windowCount: 0,
    targetWindowSec: EP_STEP_SECONDS,
    effectiveWindowSec: 0,
    latestOutputEpoch: 0,
    callbackCount: 0,
    outputCount: 0,
    phase: 'idle',
  });
  const syncPausedRef = useRef(false);
  const settingsDirtyRef = useRef(false);
  const [prefill, setPrefill] = useState<{
    state: 'idle' | 'running' | 'ready' | 'error';
    message: string;
    injected?: number;
    requested?: number;
  }>({ state: 'idle', message: '빠른 배속으로 이동한 뒤 추적 준비창을 먼저 채우세요.' });

  useEffect(() => {
    if (!open || dates.length) return;
    setLoading(true);
    fetch('/api/historical/dates')
      .then(response => response.json())
      .then(payload => {
        const filtered = (payload.dates || []).filter(
          (date: string) => date >= '20180601' && date <= '20180831',
        );
        setDates(filtered);
        HistoricalService.setPlaybackDates(filtered);
        setStatus(HistoricalService.getStatus());
        if (filtered.length && !HistoricalService.isActive()) setSelDate(filtered[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dates.length, open]);

  const markSettingsDirty = () => {
    settingsDirtyRef.current = true;
    setSettingsDirty(true);
  };

  const clearSettingsDirty = () => {
    settingsDirtyRef.current = false;
    setSettingsDirty(false);
  };

  useEffect(() => HistoricalService.subscribe(next => {
    setStatus(next);
    if (!settingsDirtyRef.current) {
      if (next.date) setSelDate(next.date);
      setSelSeconds(next.seconds);
      setSelSpeed(next.speed);
    }
  }), []);

  useEffect(() => {
    const openPlayer = () => setOpen(true);
    window.addEventListener('aees:open-historical-player', openPlayer);
    return () => window.removeEventListener('aees:open-historical-player', openPlayer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const pollTrackingSync = async () => {
      try {
        const response = await fetch('/api/twin/tracking/status');
        if (!response.ok) return;
        const payload = await response.json();
        const closedLoopStage = payload?.closed_loop_status?.stage;
        const engineCompletedNeedsRestart = Boolean(
          payload?.requires_process_restart
          || payload?.closed_loop_status?.requires_process_restart
          || closedLoopStage === 'calibration_tracking_active'
          || closedLoopStage === 'energyplus_completed_requires_restart'
          || (
            payload?.diagnostics?.phase === 'complete'
            && !payload?.running
          ),
        );
        const calibrationActive = Boolean(payload?.calibration_status?.running);
        const outputStale = Boolean(
          payload?.tracking_output_stale
          || payload?.closed_loop_status?.stage === 'energyplus_output_stale'
          || (
            payload?.running
            && payload?.diagnostics?.has_output
            && Number(payload?.diagnostics?.seconds_since_last_output ?? 0) > Number(payload?.output_stale_threshold_sec ?? 180)
          ),
        );
        const bemsTime = payload?.buffer?.bems_time ?? {};
        const targetWindowSec = Number.isFinite(bemsTime?.window_sec)
          ? Number(bemsTime.window_sec)
          : EP_STEP_SECONDS;
        const effectiveWindowSec = Number.isFinite(bemsTime?.effective_window_span_sec)
          ? Math.max(0, Number(bemsTime.effective_window_span_sec))
          : 0;
        const windowCount = Number.isFinite(bemsTime?.window_count)
          ? Math.max(0, Number(bemsTime.window_count))
          : Number.isFinite(payload?.buffer?.bems_count)
            ? Math.max(0, Number(payload.buffer.bems_count))
            : 0;
        const bufferReady = Boolean(bemsTime?.ready);
        const trackingRunning = Boolean(payload?.running);
        const trackingActive = trackingRunning && !outputStale && !engineCompletedNeedsRestart;
        const latestEpoch = Number.isFinite(payload?.latest_output?.timestamp)
          ? Number(payload.latest_output.timestamp)
          : 0;
        const callbackCount = Number.isFinite(payload?.diagnostics?.callback_count)
          ? Math.max(0, Number(payload.diagnostics.callback_count))
          : 0;
        const outputCount = Number.isFinite(payload?.diagnostics?.output_count)
          ? Math.max(0, Number(payload.diagnostics.output_count))
          : Number.isFinite(payload?.history_count)
            ? Math.max(0, Number(payload.history_count))
            : 0;
        const label = payload?.running
          ? '보정 실행 중'
          : engineCompletedNeedsRestart
            ? '보정 완료 · API 재시작 필요'
          : calibrationActive
            ? '보정 중'
            : '보정 대기';
        const syncLabel = outputStale ? '보정 출력 대기' : label;
        if (cancelled) return;
        setTrackingSync({
          active: trackingActive,
          label: syncLabel,
          running: trackingRunning,
          outputStale,
          engineCompletedNeedsRestart,
          bufferReady,
          windowCount,
          targetWindowSec,
          effectiveWindowSec,
          latestOutputEpoch: latestEpoch,
          callbackCount,
          outputCount,
          phase: payload?.diagnostics?.phase ?? 'idle',
        });
        const current = HistoricalService.getStatus();
        const currentEpoch = current.date ? historicalEpoch(current.date, current.seconds) : null;
        const hasComparableEpoch = currentEpoch != null
          && Number.isFinite(latestEpoch)
          && latestEpoch > 0
          && Math.abs(latestEpoch - currentEpoch) < 366 * 86400;
        if (outputStale && syncPausedRef.current && current.state === 'paused') {
          syncPausedRef.current = false;
          HistoricalService.setSpeed(TRACKING_SAFE_SPEED);
          setSelSpeed(TRACKING_SAFE_SPEED);
          HistoricalService.resume();
          setPrefill({
            state: 'running',
            message: '보정 출력이 다음 BEMS 샘플을 기다리고 있어 안전 속도로 다시 재생합니다.',
          });
          return;
        }
        if (trackingActive && hasComparableEpoch) {
          const aheadSec = (currentEpoch as number) - latestEpoch;
          if (current.state === 'paused' && aheadSec > EP_STEP_SECONDS * 3) {
            const aligned = historicalFrameFromEpoch(latestEpoch);
            if (current.date === aligned.date) {
              await HistoricalService.seek(aligned.seconds);
              HistoricalService.setSpeed(TRACKING_SAFE_SPEED);
              syncPausedRef.current = false;
              HistoricalService.resume();
              setSelSpeed(TRACKING_SAFE_SPEED);
              setPrefill({
                state: 'running',
                message: `보정 동기화: 재생 시각을 ${secToHMS(aligned.seconds)} 기준으로 맞췄습니다.`,
              });
              return;
            }
          }
          if (current.state === 'playing' && aheadSec > TRACKING_LOCKSTEP_PAUSE_AHEAD_SEC) {
            HistoricalService.pause();
            syncPausedRef.current = true;
            setPrefill({
              state: 'running',
              message: `보정 동기화: EnergyPlus 계산 완료까지 일시정지했습니다. 차이 ${Math.round(aheadSec / 60)}분`,
            });
            return;
          }
          if (syncPausedRef.current && current.state === 'paused' && aheadSec <= 30) {
            syncPausedRef.current = false;
            HistoricalService.resume();
            setPrefill({
              state: 'running',
              message: '보정 동기화: EnergyPlus가 현재 시점까지 따라와 재생을 이어갑니다.',
            });
            return;
          }
        } else if (!trackingActive) {
          syncPausedRef.current = false;
        }
        if (trackingActive && current.state !== 'stopped' && current.speed > TRACKING_SAFE_SPEED) {
          HistoricalService.setSpeed(TRACKING_SAFE_SPEED);
          setSelSpeed(TRACKING_SAFE_SPEED);
          setPrefill({
            state: 'error',
            message: `보정 동기화 보호: ${TRACKING_SAFE_SPEED}x로 자동 제한했습니다.`,
          });
        }
      } catch {
        if (!cancelled) {
          setTrackingSync({
            active: false,
            label: '보정 대기',
            running: false,
            outputStale: false,
            engineCompletedNeedsRestart: false,
            bufferReady: false,
            windowCount: 0,
            targetWindowSec: EP_STEP_SECONDS,
            effectiveWindowSec: 0,
            latestOutputEpoch: 0,
            callbackCount: 0,
            outputCount: 0,
            phase: 'offline',
          });
        }
      }
    };
    void pollTrackingSync();
    const id = window.setInterval(pollTrackingSync, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const handlePrefillProgress = (event: Event) => {
      const detail = (event as CustomEvent<{
        injected?: number;
        requested?: number;
        message?: string;
      }>).detail ?? {};
      const injected = Number.isFinite(detail.injected) ? Number(detail.injected) : 0;
      const requested = Number.isFinite(detail.requested) ? Number(detail.requested) : 0;
      setPrefill({
        state: 'running',
        injected,
        requested,
        message: detail.message ?? `20분 추적 준비창 적재 중: ${injected}/${requested}개 샘플`,
      });
    };
    const handlePrefillDone = (event: Event) => {
      const detail = (event as CustomEvent<{
        ok?: boolean;
        injected?: number;
        requested?: number;
        ready?: boolean;
        detail?: string;
        message?: string;
        setpointsApplied?: boolean;
        trackingStarted?: boolean;
        trackingMessage?: string;
      }>).detail ?? {};
      if (!detail.ok) {
        setPrefill({
          state: 'error',
          injected: detail.injected,
          requested: detail.requested,
          message: detail.message ?? '20분 추적 준비창 적재에 실패했습니다.',
        });
        return;
      }
      const trackingDetail = trackingMessageKo(detail.trackingMessage);
      const trackingSuffix = detail.trackingMessage
        ? ` / ${detail.trackingStarted ? 'Tracking 시작' : 'Tracking 보류'}: ${trackingDetail}`
        : '';
      const setpointSuffix = detail.setpointsApplied ? ' / 테스트 가정 set-point 적용' : '';
      setPrefill({
        state: detail.ready && (detail.trackingMessage ? detail.trackingStarted : true) ? 'ready' : 'error',
        injected: detail.injected,
        requested: detail.requested,
        message: detail.ready
          ? `준비 완료: ${detail.injected ?? 0}/${detail.requested ?? 0}개 샘플이 20분 창을 채웠습니다.${setpointSuffix}${trackingSuffix}`
          : `아직 부족: ${detail.detail ?? '20분 timestamp coverage가 부족합니다.'}`,
      });
    };
    window.addEventListener('aees:tracking-window-prefill-progress', handlePrefillProgress);
    window.addEventListener('aees:tracking-window-prefilled', handlePrefillDone);
    return () => {
      window.removeEventListener('aees:tracking-window-prefill-progress', handlePrefillProgress);
      window.removeEventListener('aees:tracking-window-prefilled', handlePrefillDone);
    };
  }, []);

  const handleApplySettings = async () => {
    if (!selDate) return;
    const playbackSpeed = Math.min(
      trackingSync.active && selSpeed > TRACKING_SAFE_SPEED ? TRACKING_SAFE_SPEED : selSpeed,
      MAX_VISUAL_PLAYBACK_SPEED,
    );
    setSelSpeed(playbackSpeed);
    clearSettingsDirty();

    const activeDate = status.date;
    if (!HistoricalService.isActive() || activeDate !== selDate) {
      await HistoricalService.start(selDate, selSeconds, playbackSpeed, dates);
      return;
    }

    await HistoricalService.seek(selSeconds);
    HistoricalService.setSpeed(playbackSpeed);
  };

  const handlePlay = async () => {
    await handleApplySettings();
  };

  const handleStartTestbed = async () => {
    const playbackDates = dates.length ? dates : [TESTBED_DATE];
    HistoricalService.setPlaybackDates(playbackDates);
    const date = playbackDates.includes(TESTBED_DATE) ? TESTBED_DATE : playbackDates[0];
    if (!date) return;
    setSelDate(date);
    setSelSeconds(TESTBED_START_SECONDS);
    setSelSpeed(TESTBED_SPEED);
    clearSettingsDirty();
    setPrefill({
      state: 'running',
      message: '09:00 운전 시작 구간으로 이동하고 08:40~09:00 BEMS 창, set-point 가정값, Tracking 시작을 준비 중입니다.',
    });
    await HistoricalService.start(date, TESTBED_START_SECONDS, TESTBED_SPEED, playbackDates);
    window.dispatchEvent(new CustomEvent('aees:prefill-tracking-window', {
      detail: {
        date,
        seconds: TESTBED_START_SECONDS,
        autoApplySetpoints: true,
        autoStartTracking: true,
        source: 'manual_testbed',
        skipDigitalTwinHandler: true,
        trackingOptions: {
          disable_throttle: false,
          max_steps: TESTBED_EP_STEPS,
          start_epoch: historicalEpoch(date, TESTBED_START_SECONDS),
          stop_after_epoch: historicalEpoch(
            date,
            TESTBED_START_SECONDS + (TESTBED_EP_STEPS * EP_STEP_SECONDS),
          ),
          run_period_days: 1,
          best_schedule_timeout_sec: TRACKING_LOCKSTEP_TIMEOUT_SEC,
          require_ready: true,
        },
      },
    }));
  };

  const handleScrub = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sec = parseInt(event.target.value, 10);
    setSelSeconds(sec);
    markSettingsDirty();
  };

  const handleSpeed = (speed: number) => {
    const nextSpeed = Math.min(
      trackingSync.active && speed > TRACKING_SAFE_SPEED ? TRACKING_SAFE_SPEED : speed,
      MAX_VISUAL_PLAYBACK_SPEED,
    );
    setSelSpeed(nextSpeed);
    markSettingsDirty();
    if (nextSpeed !== speed) {
      setPrefill({
        state: 'error',
        message: `Tracking 동기화 보호: ${TRACKING_SAFE_SPEED}x 이하로 재생해야 Tracking 결과와 그래프가 같은 시점을 유지합니다.`,
      });
    }
  };

  const handleStep = async (deltaSeconds: number) => {
    const baseSeconds = settingsDirty ? selSeconds : isActive ? status.seconds : selSeconds;
    const nextSeconds = Math.max(
      HISTORICAL_TEST_WINDOW_START_SECONDS,
      Math.min(HISTORICAL_TEST_WINDOW_END_SECONDS, baseSeconds + deltaSeconds),
    );
    setSelSeconds(nextSeconds);
    markSettingsDirty();
  };

  const isActive = status.state !== 'stopped';
  const isPlaying = status.state === 'playing';
  const progress = isActive
    ? (
      (Math.max(
        0,
        Math.min(
          HISTORICAL_TEST_WINDOW_END_SECONDS - HISTORICAL_TEST_WINDOW_START_SECONDS,
          status.seconds - HISTORICAL_TEST_WINDOW_START_SECONDS,
        ),
      ) / (HISTORICAL_TEST_WINDOW_END_SECONDS - HISTORICAL_TEST_WINDOW_START_SECONDS)) * 100
    ).toFixed(1)
    : '0';
  const currentSpeed = isActive ? status.speed : selSpeed;
  const selectedSpeed = settingsDirty ? selSpeed : currentSpeed;
  const sampleIntervalSec = Math.max(0.1, 30 / Math.max(currentSpeed, 1));
  const pendingSampleIntervalSec = Math.max(0.1, 30 / Math.max(selSpeed, 1));
  const speedLabel = currentSpeed <= 2
    ? '값 확인'
    : currentSpeed <= 5
      ? '느린 확인'
      : currentSpeed <= TRACKING_SAFE_SPEED
        ? '보정 동기화'
        : '빠른 이동';
  const rangeLabel = isActive
    && typeof status.rangeIndex === 'number'
    && status.rangeCount
    && status.rangeStart
    && status.rangeEnd
      ? `${status.rangeIndex + 1}/${status.rangeCount} · ${fmtDate(status.rangeStart)} → ${fmtDate(status.rangeEnd)}`
      : '';
  const currentEpoch = isActive && status.date ? historicalEpoch(status.date, status.seconds) : null;
  const trackingWindowProgress = trackingSync.bufferReady
    ? 100
    : Math.min(100, Math.max(0, (trackingSync.effectiveWindowSec / Math.max(trackingSync.targetWindowSec, 1)) * 100));
  const expectedWindowSamples = Math.max(1, Math.round(trackingSync.targetWindowSec / 30) + 1);
  const prefillProgress = prefill.requested
    ? Math.min(100, Math.max(0, ((prefill.injected ?? 0) / prefill.requested) * 100))
    : 0;
  const displayWindowProgress = Math.max(trackingWindowProgress, prefillProgress);
  const displayWindowCount = Math.max(trackingSync.windowCount, prefill.injected ?? 0);
  const displayExpectedSamples = Math.max(expectedWindowSamples, prefill.requested ?? 0);
  const displayEffectiveWindowSec = trackingSync.effectiveWindowSec > 0
    ? trackingSync.effectiveWindowSec
    : (displayWindowProgress / 100) * trackingSync.targetWindowSec;
  const displayWindowReady = trackingSync.bufferReady || displayWindowProgress >= 99;
  const trackingLagSec = currentEpoch != null && trackingSync.latestOutputEpoch > 0
    ? currentEpoch - trackingSync.latestOutputEpoch
    : null;
  const trackingCatchupProgress = trackingSync.running
    ? trackingLagSec == null
      ? 12
      : Math.min(100, Math.max(0, ((EP_STEP_SECONDS - Math.max(0, trackingLagSec)) / EP_STEP_SECONDS) * 100))
    : trackingSync.latestOutputEpoch > 0
      ? 100
      : 0;
  const latestTrackingTime = trackingSync.latestOutputEpoch > 0
    ? secToHMS(historicalFrameFromEpoch(trackingSync.latestOutputEpoch).seconds)
    : '--:--:--';
  const trackingLagLabel = trackingLagSec == null
    ? '출력 대기'
    : Math.abs(trackingLagSec) <= 30
      ? '동기화됨'
      : trackingLagSec > 0
        ? `${Math.round(trackingLagSec / 60)}분 지연`
        : `${Math.round(Math.abs(trackingLagSec) / 60)}분 선행`;

  const handlePrepareTrackingWindow = () => {
    if (!isActive || !status.date) return;
    if (HistoricalService.isPlaying()) HistoricalService.pause();
    setPrefill({ state: 'running', message: '현재 시점 직전 20분 BEMS 창을 DataBuffer에 적재 중입니다.' });
    window.dispatchEvent(new CustomEvent('aees:prefill-tracking-window', {
      detail: { date: status.date, seconds: status.seconds, skipDigitalTwinHandler: true },
    }));
  };

  if (!open && !isActive) return null;

  return (
    <div className={`fixed z-50 ${open
      ? `left-1/2 -translate-x-1/2 ${activeTab === 'overview' ? 'bottom-40 md:bottom-20' : 'bottom-20 md:bottom-6'}`
      : 'bottom-4 left-[92px] translate-x-0 md:left-[112px]'
    }`}>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title={isActive ? `과거 BEMS 재생 · ${fmtDate(status.date)} ${secToHMS(status.seconds)} · ${status.speed}x` : '과거 BEMS 데이터 열기'}
          className={`flex items-center gap-2 rounded-full border px-3.5 py-2 text-[9px] font-orbitron font-bold uppercase tracking-wider shadow-lg transition-all ${
            isActive
              ? 'animate-pulse border-amber-500/50 bg-amber-500/20 text-amber-400 shadow-amber-500/20'
              : 'border-white/10 bg-black/60 text-slate-400 backdrop-blur-xl hover:border-white/20 hover:text-white'
          }`}
        >
          <i className={`fa-solid ${isActive ? 'fa-circle-play' : 'fa-clock-rotate-left'} text-sm`} />
          {isActive
            ? `HIST · ${secToHMS(status.seconds)} · ${status.speed}x`
            : '과거 데이터'}
        </button>
      )}

      {open && (
        <div className="glass-panel w-[min(600px,calc(100vw-24px))] space-y-4 rounded-lg border border-white/10 bg-black/80 px-5 py-4 shadow-[0_20px_60px_rgba(0,0,0,0.6)] backdrop-blur-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`flex h-8 w-8 items-center justify-center rounded-xl border ${
                isActive
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
                  : 'border-white/10 bg-white/5 text-slate-400'
              }`}>
                <i className="fa-solid fa-clock-rotate-left text-sm" />
              </div>
              <div>
                <h4 className="text-[10px] font-orbitron font-bold uppercase tracking-widest text-white">
                  과거 BEMS 데이터
                </h4>
                <p className="mt-0.5 text-[8px] font-mono text-slate-500">
                  {isActive
                    ? `실측 데이터 재생 중 / ${status.pointCount} points`
                    : 'SEDC 실측 BMS 데이터 (2018 여름 / 6-8월)'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex h-7 w-7 items-center justify-center text-slate-500 transition-colors hover:text-white"
              aria-label="Close historical player"
            >
              <i className="fa-solid fa-chevron-down text-xs" />
            </button>
          </div>

          {isActive && (
            <div className="flex items-center gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-2.5">
              <div className={`h-2 w-2 rounded-full ${isPlaying ? 'animate-pulse bg-amber-400' : 'bg-slate-500'}`} />
              <span className="flex-1 text-[9px] font-orbitron font-bold uppercase tracking-widest text-amber-400">
                {isPlaying ? '재생 중' : '일시정지'} / {fmtDate(status.date)} {secToHMS(status.seconds)}
              </span>
              <span className="font-mono text-[8px] text-amber-500/70">{status.speed}x · {sampleIntervalSec.toFixed(1)}초/샘플</span>
              {rangeLabel && (
                <span className="hidden font-mono text-[8px] text-amber-500/70 sm:inline">
                  {rangeLabel}
                </span>
              )}
              {settingsDirty && (
                <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[8px] font-bold text-slate-950">
                  적용 필요
                </span>
              )}
              <button
                type="button"
                onClick={() => HistoricalService.stop()}
                className="text-xs text-slate-500 transition-colors hover:text-red-400"
                aria-label="Stop historical playback"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          )}

          {isActive && (
            <div className="grid gap-3 rounded-xl border border-cyan-500/20 bg-cyan-500/10 px-4 py-3 sm:grid-cols-2">
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-[9px] font-orbitron font-bold uppercase tracking-widest text-cyan-300">
                    20분 데이터 창
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[8px] font-bold ${
                    displayWindowReady ? 'bg-emerald-400 text-slate-950' : 'bg-amber-400 text-slate-950'
                  }`}>
                    {displayWindowReady ? '준비 완료' : '수집 중'}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                  <div
                    className="h-full rounded-full bg-cyan-300 transition-[width]"
                    style={{ width: `${displayWindowProgress}%` }}
                  />
                </div>
                <p className="mt-2 font-mono text-[8px] text-slate-400">
                  샘플 {displayWindowCount}/{displayExpectedSamples}개 · {Math.min(Math.round(displayEffectiveWindowSec), trackingSync.targetWindowSec)}초/{trackingSync.targetWindowSec}초
                </p>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-[9px] font-orbitron font-bold uppercase tracking-widest text-cyan-300">
                    추적 진행
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[8px] font-bold ${
                    trackingSync.active
                      ? 'bg-cyan-300 text-slate-950'
                      : trackingSync.outputStale || trackingSync.engineCompletedNeedsRestart
                        ? 'bg-amber-400 text-slate-950'
                        : 'bg-slate-700 text-slate-300'
                  }`}>
                    {trackingSync.label}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                  <div
                    className={`h-full rounded-full transition-[width] ${
                      trackingSync.active ? 'bg-emerald-300' : 'bg-slate-500'
                    }`}
                    style={{ width: `${trackingCatchupProgress}%` }}
                  />
                </div>
                <p className="mt-2 font-mono text-[8px] text-slate-400">
                  최근 출력 {latestTrackingTime} · {trackingLagLabel} · 출력 {trackingSync.outputCount} step
                </p>
              </div>
            </div>
          )}

          {!isActive && (
            <div className="space-y-3">
              <div>
                <label className="mb-1.5 block text-[8px] font-orbitron font-bold uppercase tracking-widest text-slate-500">
                  날짜 선택
                </label>
                {loading ? (
                  <div className="h-9 animate-pulse rounded-xl bg-white/5" />
                ) : (
                  <select
                    value={selDate}
                    onChange={event => {
                      setSelDate(event.target.value);
                      markSettingsDirty();
                    }}
                    className="w-full rounded-xl border border-white/10 bg-slate-900/80 px-3 py-2 font-mono text-[10px] text-slate-200 focus:border-amber-500/50 focus:outline-none"
                  >
                    {dates.length === 0 && (
                      <option value="">서버를 먼저 실행하세요</option>
                    )}
                    {[...dates].reverse().map(date => (
                      <option key={date} value={date}>{fmtDate(date)}</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <div className="mb-1.5 flex justify-between">
                  <label className="text-[8px] font-orbitron font-bold uppercase tracking-widest text-slate-500">
                    시작 시각
                  </label>
                  <span className="text-[9px] font-orbitron text-amber-400">{secToHMS(selSeconds)}</span>
                </div>
                <input
                  type="range"
                  min={HISTORICAL_TEST_WINDOW_START_SECONDS}
                  max={HISTORICAL_TEST_WINDOW_END_SECONDS}
                  step={30}
                  value={selSeconds}
                  onChange={event => {
                    setSelSeconds(parseInt(event.target.value, 10));
                    markSettingsDirty();
                  }}
                  className="h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-slate-800 accent-amber-500"
                />
                <div className="mt-1 flex justify-between font-mono text-[7px] text-slate-600">
                  <span>08:00</span><span>09:00 HVAC ON</span><span>12:00</span><span>18:00 OFF</span><span>19:00</span>
                </div>
              </div>

              <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/10 px-3 py-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="flex-1">
                    <p className="text-[9px] font-orbitron font-bold uppercase tracking-widest text-cyan-300">
                      보정 테스트베드
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleStartTestbed}
                    disabled={!selDate || prefill.state === 'running'}
                    className="flex min-h-9 items-center justify-center gap-2 rounded-xl bg-cyan-400 px-3 text-[9px] font-orbitron font-bold uppercase tracking-widest text-slate-950 transition-all hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className={`fa-solid ${prefill.state === 'running' ? 'fa-circle-notch fa-spin' : 'fa-flask-vial'}`} />
                    테스트베드 시작
                  </button>
                </div>
              </div>
            </div>
          )}

          {isActive && (
            <div>
              <div className="mb-1.5 flex justify-between">
                <span className="font-mono text-[8px] text-slate-500">07:00:00</span>
                <span className="text-[9px] font-orbitron font-bold text-amber-400">
                  {secToHMS(status.seconds)}
                </span>
                <span className="font-mono text-[8px] text-slate-500">19:00:00</span>
              </div>
              <div className="relative">
                <div
                  className="pointer-events-none absolute left-0 top-0 h-1.5 rounded-lg bg-amber-500/40"
                  style={{ width: `${progress}%` }}
                />
                <input
                  type="range"
                  min={HISTORICAL_TEST_WINDOW_START_SECONDS}
                  max={HISTORICAL_TEST_WINDOW_END_SECONDS}
                  step={30}
                  value={selSeconds}
                  onChange={handleScrub}
                  className="relative h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-slate-800 accent-amber-500"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-4 gap-2">
            {[
              { label: '-20분', delta: -1200 },
              { label: '-30초', delta: -30 },
              { label: '+30초', delta: 30 },
              { label: '+20분', delta: 1200 },
            ].map(item => (
              <button
                key={item.label}
                type="button"
                onClick={() => void handleStep(item.delta)}
                className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[9px] font-orbitron font-bold text-slate-300 transition hover:bg-white/10 hover:text-white"
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="mr-1 text-[8px] font-orbitron uppercase text-slate-500">재생 속도</span>
            {SPEEDS.map(speed => (
              <button
                key={speed}
                type="button"
                onClick={() => handleSpeed(speed)}
                disabled={trackingSync.active && speed > TRACKING_SAFE_SPEED}
                className={`rounded-lg px-3 py-1.5 text-[9px] font-orbitron font-bold transition-all ${
                  selectedSpeed === speed
                    ? 'bg-amber-500 text-slate-950'
                    : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white'
                } disabled:cursor-not-allowed disabled:opacity-35 ${
                  trackingSync.active && speed > TRACKING_SAFE_SPEED ? 'line-through' : ''
                }`}
              >
                {speed}x
              </button>
            ))}
            <span className={`ml-auto rounded-full border px-2 py-1 text-[8px] font-semibold ${
              trackingSync.active
                ? 'border-cyan-400/30 bg-cyan-400/10 text-cyan-200'
                : 'border-white/10 bg-white/5 text-slate-500'
            }`}>
              {settingsDirty
                ? `적용 후 ${pendingSampleIntervalSec.toFixed(1)}초마다 값 변경`
                : `${speedLabel} · ${sampleIntervalSec.toFixed(1)}초마다 값 변경`}
            </span>
          </div>

          {isActive && (
            <div className={`rounded-xl border px-3 py-2.5 ${
              prefill.state === 'ready'
                ? 'border-emerald-500/25 bg-emerald-500/10'
                : prefill.state === 'error'
                  ? 'border-amber-500/25 bg-amber-500/10'
                  : 'border-cyan-500/20 bg-cyan-500/10'
            }`}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={handlePrepareTrackingWindow}
                  disabled={prefill.state === 'running' || status.seconds < 30}
                  className="flex min-h-9 items-center justify-center gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/15 px-3 text-[9px] font-orbitron font-bold uppercase tracking-widest text-cyan-300 transition-all hover:bg-cyan-500/25 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className={`fa-solid ${prefill.state === 'running' ? 'fa-circle-notch fa-spin' : 'fa-database'}`} />
                  보정 준비 20분 적재
                </button>
                <p className="flex-1 text-[8px] leading-4 text-slate-400">
                  {prefill.message}
                </p>
              </div>
              {currentSpeed > TRACKING_SAFE_SPEED && (
                <p className="mt-2 text-[8px] leading-4 text-amber-300/75">
                  보정 중에는 {TRACKING_SAFE_SPEED}x 이하로 유지해야 그래프와 상단 KPI가 같은 시간대를 봅니다.
                </p>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            {!isActive ? (
              <button
                type="button"
                onClick={handlePlay}
                disabled={!selDate}
                className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-amber-500 py-3 text-[10px] font-orbitron font-bold uppercase tracking-widest text-slate-950 transition-all hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <i className="fa-solid fa-play" />
                설정 적용/재생 시작
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => void handleApplySettings()}
                  disabled={!settingsDirty}
                  className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-cyan-400 py-3 text-[10px] font-orbitron font-bold uppercase tracking-widest text-slate-950 transition-all hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-35"
                >
                  <i className="fa-solid fa-check" />
                  설정 적용
                </button>
                <button
                  type="button"
                  onClick={() => (isPlaying ? HistoricalService.pause() : HistoricalService.resume())}
                  className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-amber-500 py-3 text-[10px] font-orbitron font-bold uppercase tracking-widest text-slate-950 transition-all hover:bg-amber-400"
                >
                  <i className={`fa-solid ${isPlaying ? 'fa-pause' : 'fa-play'}`} />
                  {isPlaying ? '일시정지' : '재개'}
                </button>
                <button
                  type="button"
                  onClick={() => HistoricalService.stop()}
                  className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 text-[10px] font-orbitron font-bold uppercase tracking-widest text-slate-400 transition-all hover:bg-white/10 hover:text-white"
                >
                  <i className="fa-solid fa-stop mr-1.5" />
                  정지/날짜 변경
                </button>
              </>
            )}
          </div>

          <p className="text-center font-mono text-[8px] text-slate-600">
            {isActive
              ? '30초 단위 실측 샘플로 대시보드 전체를 갱신 중입니다. 값 확인은 1x-5x 또는 일시정지를 권장합니다.'
              : '재생 중에는 시뮬레이션 값 대신 실측 BEMS 값이 표시됩니다.'}
          </p>
        </div>
      )}
    </div>
  );
};
